/**
 * ANA-1: analytics for the message search (SRE-3328), on the front.
 *
 * Screen diagnostics of the two searches: what people look for and how much the
 * search is used. Both events are gestures only the web app sees ("the query
 * the person settled on", "opened the bar"), so neither has a server-side twin.
 * Names follow `front.<domain>.<entity>.<action>`.
 */
export const MESSAGE_SEARCH_EVENTS = {
  /**
   * The in-chat search bar went from closed to open. Carries how (`trigger`)
   * and the session its `searched` events will share.
   */
  OPENED: 'front.chat.message_search.opened',

  /**
   * One SETTLED query: the server answered it, and the person either acted on
   * its results or left them on screen for MESSAGE_SEARCH_SETTLE_MS. Never one
   * per request: the search asks on every pause in typing, so a typed word is
   * two or three requests and one event. Carries the query MASKED (see
   * maskSearchQuery), never as typed.
   */
  SEARCHED: 'front.chat.message_search.searched',
} as const;

/** Union of every valid message-search analytics event name. */
export type tMessageSearchEventName =
  (typeof MESSAGE_SEARCH_EVENTS)[keyof typeof MESSAGE_SEARCH_EVENTS];

/**
 * How long a query's results stay on screen, untouched, before the query counts
 * as settled. Shorter, and the fragments someone pauses on mid-word ("fac",
 * "factu") become searches; the search itself already waits 500 ms per pause.
 */
export const MESSAGE_SEARCH_SETTLE_MS = 2000;

/**
 * Which search, the `surface` property of SEARCHED: the inbox box's Mensajes
 * section, or "Buscar en esta conversación". The screen a conversation search
 * ran on (Chats, Embudos, Directorio) is `$pathname`, which every event carries.
 */
export const MESSAGE_SEARCH_SURFACES = ['inbox', 'conversation'] as const;

export type tMessageSearchSurface = (typeof MESSAGE_SEARCH_SURFACES)[number];

/**
 * What settled a query, the `settled_by` property of SEARCHED.
 *
 * - `paused`: MESSAGE_SEARCH_SETTLE_MS went by with its results on screen.
 * - `result_opened`: a result of the inbox search was opened.
 * - `stepped`: the next or the previous match was asked for in the conversation.
 * - `cleared`: the inbox box was emptied.
 * - `closed`: the in-chat bar was closed (Esc, "Cerrar").
 * - `left`: the search went away under the person: another contact opened, or
 *   the screen was left.
 */
export const MESSAGE_SEARCH_SETTLED_BY = [
  'paused',
  'result_opened',
  'stepped',
  'cleared',
  'closed',
  'left',
] as const;

export type tMessageSearchSettledBy =
  (typeof MESSAGE_SEARCH_SETTLED_BY)[number];

/** How the in-chat bar was opened: the magnifier, or Ctrl/⌘+F. */
export const MESSAGE_SEARCH_OPEN_TRIGGERS = ['button', 'shortcut'] as const;

export type tMessageSearchOpenTrigger =
  (typeof MESSAGE_SEARCH_OPEN_TRIGGERS)[number];

/**
 * What a masked query is made of, the `query_kind` property of SEARCHED: a
 * token's kind when the query is nothing but tokens of that one kind,
 * punctuation aside; `mixed` when it has words too, or tokens of several
 * kinds; `text` when it has no token at all.
 */
export const MESSAGE_SEARCH_QUERY_KINDS = [
  'text',
  'phone',
  'email',
  'url',
  'number',
  'mixed',
] as const;

export type tMessageSearchQueryKind =
  (typeof MESSAGE_SEARCH_QUERY_KINDS)[number];

export type tMessageSearchSearchedProps = {
  surface: tMessageSearchSurface;
  /** The query after maskSearchQuery. The only free text any event carries. */
  query_masked: string;
  query_kind: tMessageSearchQueryKind;
  /** Runes of the query as typed, before masking and before the length cap. */
  query_length: number;
  word_count: number;
  /** At least one result was loaded for the query when it settled. */
  has_results: boolean;
  /** Results of the first page. There is no total: the server never counts. */
  first_page_count: number;
  /** The server had more pages to give when the query settled. */
  has_more: boolean;
  settled_by: tMessageSearchSettledBy;
  /** Groups the queries of one search: one stay in the box, one open bar. */
  search_session_id: string;
};

export type tMessageSearchOpenedProps = {
  trigger: tMessageSearchOpenTrigger;
  search_session_id: string;
};
